import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	Stack,
	SvgIcon,
	Typography,
} from '@mui/material';
import React from 'react';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon, { type IconName } from '../../utils/getMuiIcon';
import useScreenSize from '../../utils/useScreenSize';

export interface CustomBannerProps {
	open: boolean;
	Image: React.ElementType;
	title: string;
	subtitle?: string;
	additionalInfo?: React.ReactNode;
	button: {
		text: string;
		onClick: () => void;
		icon?: IconName;
	};
	dialogProps?: Partial<DialogProps>;
	onClose?: () => void;
}

const CustomBanner: React.FC<CustomBannerProps> = (props) => {
	const { open, Image, title, subtitle, additionalInfo, button, dialogProps, onClose } = props;
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isMobile = isXsScreen || isSmScreen;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			data-testid="custom-banner"
			{...dialogProps}
		>
			<DialogContent
				sx={{
					padding: isMobile ? spaces.md : spaces.lg,
				}}
			>
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: spaces.md,
					}}
				>
					{!isMobile && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								maxHeight: '300px',
								aspectRatio: '4/3',
								'& svg': {
									width: '100%',
									height: '100%',
								},
							}}
						>
							<Image />
						</Box>
					)}
					<Typography
						variant={isMobile ? 'h6' : 'h5'}
						sx={{
							color: palette.text.primary,
						}}
					>
						{title}
					</Typography>
					{subtitle && (
						<Typography
							variant={isMobile ? 'body2' : 'body1'}
							sx={{
								color: palette.text.secondary,
							}}
						>
							{subtitle}
						</Typography>
					)}
					{additionalInfo && <>{additionalInfo}</>}
				</Stack>
			</DialogContent>
			<DialogActions
				sx={{
					padding: isMobile ? spaces.md : spaces.lg,
				}}
			>
				<Button
					data-testid="custom-banner-button"
					onClick={button.onClick}
					variant="contained"
					color="primary"
					size="large"
					fullWidth
					type="button"
					aria-label={button.text}
					startIcon={
						button.icon ? (
							<SvgIcon component={getMuiIcon(button.icon)} data-testid="icon-button" aria-hidden="true" />
						) : undefined
					}
				>
					{button.text}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CustomBanner;
